// The guided tour of time logging, for people arriving from an external
// timesheet.
//
// Time gets logged in two places and the tour says so: the Log time panel in
// the sidebar is the everyday path (it is on every screen, so an hour can be
// filed the moment it is finished), and this page is where a week is reviewed,
// corrected and caught up. A tour that described only the page would teach the
// slower of the two and leave the other undiscovered.
//
// Steps point at the real screen via [data-tour] attributes on MyTimePage, plus
// the sidebar trigger in shared/QuickTimeLog.jsx. The engine
// (shared/FeatureTour.jsx) and the show-once record (hooks/useFeatureTour.js)
// are generic; only this file knows about time.
//
// Wording note: several targets only render in the right conditions (the amber
// "days are short" banner, the whole entry form). The engine shows those steps
// centred when the element is missing, so each body is written to read
// standalone as well as pointed.

export const TIME_LOGGING_TOUR_ID = 'time-logging';

// Two stretches of this tour describe things that are not on screen until
// something is opened: the entry form (a modal) and the quick panel. Rather than
// leave that to chance, every step declares the state it needs, so stepping
// backwards through the tour lands the screen where stepping forwards did. All
// four helpers are idempotent.
const openEntryModal = () => {
  if (document.querySelector('[data-tour="entry-modal"]')) return;
  document.querySelector('[data-tour="add-entry"]')?.click();
};
const closeEntryModal = () => {
  document.querySelector('[data-tour="entry-modal-close"]')?.click();
};

// The panel is a toggle on one button, so opening and closing are the same
// click guarded by whether the panel is already there. Both also shut the entry
// modal: the quick logger is the alternative to it, and the two overlapping
// would be nonsense on any step that mentions either.
const openQuickLog = () => {
  closeEntryModal();
  if (document.querySelector('[data-tour="quick-panel"]')) return;
  document.querySelector('[data-tour="quick-time-log"]')?.click();
};
const closeQuickLog = () => {
  closeEntryModal();
  if (!document.querySelector('[data-tour="quick-panel"]')) return;
  document.querySelector('[data-tour="quick-time-log"]')?.click();
};

export const timeLoggingTourSteps = [
  {
    title: 'Welcome to My Time',
    before: closeQuickLog,
    body: [
      'Your time now lives here. This tour shows you where your week went, how to catch up a day you missed, and the two-tap way to log an hour without leaving the screen you are on.',
      'Use Next (or the arrow keys) to walk through, or Skip if you would rather explore. Either way it will not pop up again.',
    ],
  },
  {
    title: 'Day, week or month',
    target: '[data-tour="view-toggle"]',
    before: closeEntryModal,
    body: [
      'Week is the one most people live in: five columns, and it is obvious at a glance which of them are still empty.',
      'The ‹ › arrows move a period at a time and Today brings you back.',
    ],
  },
  {
    title: 'Where your hours went',
    target: '[data-tour="summary"]',
    before: closeEntryModal,
    body: [
      'Your total for the period, split into tasks, projects and overheads. Project time is what ends up against a SOW\'s cost and profitability, so this is worth a glance before the month closes.',
    ],
  },
  {
    title: 'Where the week actually went',
    target: '[data-tour="breakdown"]',
    before: closeEntryModal,
    body: [
      'The tiles above say how much of the week was project time. Breakdown says which projects: a row for every task, project and overhead category you logged against, a column for every day, and totals down the side and along the bottom.',
      'It is the quickest way to spot an afternoon filed against the wrong job, and it follows whichever period you are looking at.',
    ],
  },
  {
    title: 'Your week, day by day',
    target: '[data-tour="calendar"]',
    before: closeEntryModal,
    body: [
      'One column per day, with every entry on it. Hover an entry to edit or delete it, and use + Add at the foot of a column to log against that day.',
      'Click a day header to select it, or several, then add one entry across all of them at once.',
      'Days you were on leave, off sick or on a bank holiday show as exactly that. They fill the day without counting as work, so nothing is owed against them. A half day off fills its half: the other half is still expected, at half the usual hours.',
      'Approved leave and sickness also put an entry on the day for you, marked Leave, at your usual hours for that day. It follows the leave request: change or cancel the request and the entry moves or goes with it, so there is nothing to edit or delete here.',
    ],
  },
  {
    title: 'Days that are still short',
    target: '[data-tour="missing-days"]',
    before: closeEntryModal,
    body: [
      'A weekday that is here or past and under a full day is flagged amber, both on the day and in a banner above the week.',
      '"Log the missing days" opens the form with every short day already selected, so catching up a week is one entry rather than five.',
    ],
  },
  {
    title: 'Adding time by hand',
    target: '[data-tour="add-entry"]',
    before: closeEntryModal,
    body: [
      'Add Entry opens the full form. It has more in it than the quick panel: several dates at once, and a milestone to tag the time to.',
      'The next few steps walk through it.',
    ],
  },
  {
    title: 'One entry, several days',
    target: '[data-tour="entry-dates"]',
    before: openEntryModal,
    body: [
      'Tap the weekday buttons to pick days, or "This week (Mon–Fri)" for the lot. The date picker adds any other date.',
      'Each date selected gets its own entry, so a three-day job is logged once and filed three times.',
    ],
  },
  {
    title: 'Your usual work, one tap away',
    target: '[data-tour="entry-favourites"]',
    before: openEntryModal,
    body: [
      'Star the projects, tasks and categories you log against most and they appear here as buttons, in this form and in the quick panel.',
      'If you log against the same handful of things every week, this is the step that saves you the most time.',
    ],
  },
  {
    title: 'Task, project or overhead',
    target: '[data-tour="entry-type"]',
    before: openEntryModal,
    body: [
      'Project time is against a SOW and carries a cost to that job. Task time is against something assigned to you. Overhead is everything else: absence, business development, training, line management and the rest.',
      'Pick the most specific category that fits. "Other" is a last resort, and when you use it the note is not optional: an hour against "Other" with nothing written on it cannot be explained by anybody later.',
      'Pick a SOW that has not been issued yet and the form says so: that is bid and SOW creation work, logged as such, still tagged to the SOW so it counts toward its cost once it goes live.',
    ],
  },
  {
    title: 'Which part of the job',
    target: '[data-tour="entry-type"]',
    before: openEntryModal,
    body: [
      'Pick a project that has milestones and a Milestone box appears. Hours tagged to a milestone cost against it, which is what makes the actuals on a project mean anything; hours with no milestone cost against nothing and quietly disappear from the figures.',
      'The milestones still being delivered are listed first, with the finished ones below, because late work against one of those is real and still needs logging.',
      'If the time genuinely belongs to no milestone, that is allowed, but the form asks you to say why in the notes and flags the entry for review. Somebody reads those, and "no reason given" is the one that costs them an afternoon.',
    ],
  },
  {
    title: 'Hours without the arithmetic',
    target: '[data-tour="entry-hours"]',
    before: openEntryModal,
    body: [
      'The buttons cover the common amounts. "Full day" is your contracted day, and "Rest of day" is whatever is left of it after what you have already logged, so you never have to do the subtraction yourself.',
      'Anything else goes in the box, in quarter hours.',
    ],
  },
  {
    title: 'Say what it was, then log it',
    target: '[data-tour="entry-notes"]',
    before: openEntryModal,
    body: [
      'Notes are optional but worth it. An hour with no note cannot be explained by anybody a month later, including you.',
      'Log files the entry, or Log (3 days) if you picked three dates.',
    ],
  },
  {
    title: 'Your week closes on Monday night',
    before: closeEntryModal,
    body: [
      'When the Monday after a week ends, that whole week closes (a bank holiday Monday hands the job to the next working day). Nothing in it can be added, amended or deleted afterwards, and its entries show a padlock, because the numbers behind them have already been reported on.',
      'So Friday is still the day to check the week is complete: the amber flags above are exactly what to clear before you finish. If the week is short on Monday morning you will get an email, and you have until the end of that day to put it right. If something in a closed week genuinely needs fixing, ask an admin, HR or PMO.',
    ],
  },
  {
    title: 'Now the fast way',
    target: '[data-tour="quick-time-log"]',
    before: closeQuickLog,
    body: [
      'Everything so far needed you to be on this page. Log time sits in the sidebar on every screen, so an hour can be filed the moment you finish it rather than reconstructed on Friday afternoon.',
      'The badge on it shows what you have logged today, which is usually the reminder you needed.',
    ],
  },
  {
    title: 'What you worked on',
    target: '[data-tour="quick-what"]',
    before: openQuickLog,
    body: [
      'The same three choices as the full form, in a smaller space: a task, a project, or an overhead category. Your favourites sit just above, so the usual job is one tap.',
      'The date is at the top and starts on today. If the day you pick turns out to be leave, sickness or a bank holiday, the panel says so rather than letting you log against it by accident.',
    ],
  },
  {
    title: 'Hours, and your day so far',
    target: '[data-tour="quick-hours"]',
    before: openQuickLog,
    body: [
      'The same shortcuts as the form, with a bar underneath showing how much of your day is already accounted for. That is the bit worth watching: it answers "have I logged enough today?" without you opening anything.',
    ],
  },
  {
    title: 'Log it, then log the next one',
    target: '[data-tour="quick-panel"]',
    before: openQuickLog,
    body: [
      'Log Time files it and the panel stays open, with the job and the date still set, because a day is rarely one thing. Only the hours and the note clear.',
      'Anything you file here appears on this page straight away, and vice versa.',
    ],
  },
  {
    title: 'The milestone question, here too',
    target: '[data-tour="quick-milestone"]',
    before: openQuickLog,
    body: [
      'Choose a project with milestones and the panel asks which one, exactly as the full form does, and warns you in amber if you have not picked one.',
      'Logging without a milestone still works. Write the reason in the notes and the entry goes through, flagged for review.',
    ],
  },
  {
    title: 'That\'s everything',
    before: closeQuickLog,
    body: [
      'Log as you go from the sidebar, and use this page to check the week is complete. If you want this walkthrough again, the "?" beside the My Time heading replays it any time.',
    ],
  },
];
